import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import { PointCloudOctree } from '@datalabs-jp/three-loader'
import { EDITOR_POINT_SIZE_INTERVAL } from '@editor/config/constants'
import { useGlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import usePCDLoader from '@editor/pages/projects/hooks/usePCDLoader'
import { loadAllPCDs } from '@editor/pages/projects/pcd-alignment/utils'
import { getBlueprints } from '@editor/services/Blueprint'
import { getCenterAndRadius } from '@editor/services/Editor'
import { generateCuboidUniforms } from '@editor/services/Points'
import { autoCalculatePrintScale } from '@editor/services/PrintScale'
import { withPromiseContained } from '@editor/services/Util'
import { RootState, useAppDispatch } from '@editor/store/app'
import { selectConnectedAreas } from '@editor/store/selectors'
import * as Sentry from '@sentry/react'
import { clamp } from 'lodash'
import mixpanel from 'mixpanel-browser'
import { useSelector } from 'react-redux'
import { Matrix4, NormalBlending, Points, PointsMaterial, Scene, Vector3 } from 'three'

import { useBlueprintEditorContext } from '../contexts/BlueprintEditorContext'
import { BlueprintEditorTools, Mode } from '../definitions'
import CanvasGrid from '../objects/CanvasGrid'
import {
  setDefaultPointSize,
  setIsPointCloud2dLoading,
  setPcd2dBoundingBox,
  setPcdBoundingBox,
  setPcdBoundingSphereRadius,
  setPointSize,
  setSelectedBlueprint,
  updateBlueprint,
  updateBlueprints,
} from '../store/blueprintEditor'
import { SelectionStage } from '../tools/BlueprintManager/store'
import { getRunningPcdStatus } from '../utils'
import RenderLoop from './RenderLoop'

const PCDManager = ({ sceneRef }: { sceneRef: React.RefObject<Scene | null> }) => {
  // Contexts
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useGlobalModalContext()

  // Hooks
  const { potree, loadPCD } = usePCDLoader()
  const { pointClouds, pointCloud2ds, arcballControls, setPointClouds, setPointCloud2ds } = useBlueprintEditorContext()

  // Store
  const dispatch = useAppDispatch()
  const project = useSelector((state: RootState) => state.page.project)
  const inspectionArea = useSelector((state: RootState) => state.page.inspectionArea)
  const connectedAreas = useSelector(selectConnectedAreas)
  const initCompleted = useSelector((state: RootState) => state.blueprint.editor.initCompleted)
  const mode = useSelector((state: RootState) => state.blueprint.editor.mode)

  const previousSelectedBlueprintId = useSelector(
    (state: RootState) => state.blueprint.editor.previousSelectedBlueprintId,
  )
  const selectedBlueprint = useSelector((state: RootState) => state.blueprint.editor.selectedBlueprint)
  const isCreatingBlueprint = useSelector((state: RootState) => state.blueprint.editor.isCreatingBlueprint)

  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)
  const prevSelectedTool = useSelector((state: RootState) => state.blueprint.editor.prevSelectedTool)

  const pointSize = useSelector((state: RootState) => state.blueprint.editor.pointSize)
  const isPointCloudInvisible = useSelector((state: RootState) => state.blueprint.editor.isPointCloudInvisible)
  const pcdBoundingSphereRadius = useSelector((state: RootState) => state.blueprint.editor.pcdBoundingSphereRadius)
  const pcdTransparency = useSelector((state: RootState) => state.blueprint.editor.pcdTransparency)
  const pcdBoundingBox = useSelector((state: RootState) => state.blueprint.editor.pcdBoundingBox)
  const pcd2dBoundingBox = useSelector((state: RootState) => state.blueprint.editor.pcd2dBoundingBox)

  const workingMask = useSelector((state: RootState) => state.maskPCD.workingMask)
  const selectionStage = useSelector((state: RootState) => state.bpBlueprintCreator.selectionStage)

  // Refs
  const initializedBlueprintId2DPcd = useRef<string | null>(null)

  const load2DPointCloud = useCallback(() => {
    const runningPcd = getRunningPcdStatus(selectedBlueprint)
    if (
      pointCloud2ds.length > 0 ||
      !inspectionArea ||
      !selectedBlueprint?.sort_key ||
      !connectedAreas[0]?.current.blueprint ||
      runningPcd.length ||
      (initializedBlueprintId2DPcd.current && initializedBlueprintId2DPcd.current === selectedBlueprint.blueprint_id) // Already loaded
    )
      return

    initializedBlueprintId2DPcd.current = selectedBlueprint.blueprint_id
    dispatch(setIsPointCloud2dLoading(true))

    const loadStartTime = Date.now()
    void withPromiseContained(async () => {
      const pcds = (await Promise.all(loadAllPCDs(connectedAreas, inspectionArea, loadPCD))).filter((p) => p?.pcd) as {
        areaId: string
        pcd: Points | PointCloudOctree
      }[]

      if (!pcds.length) return

      // get bounding box
      const { bbox } = getCenterAndRadius(pcds.map((p) => p.pcd))
      dispatch(setPcd2dBoundingBox([bbox.min.toArray(), bbox.max.toArray()]))

      // store pcds and props
      setPointCloud2ds(pcds.map((p) => p.pcd))

      // set default point size
      pcds.forEach(({ pcd }) => {
        if (pcd instanceof PointCloudOctree) pcd.material.size = pointSize / 2
      })
    }).finally(() => {
      dispatch(setIsPointCloud2dLoading(false))

      // Track PCD File Load Performance
      const loadDuration = Date.now() - loadStartTime
      mixpanel.track('PCD File Load Performance', {
        Type: '2D',
        'Duration (ms)': loadDuration,
        'Blueprint ID': selectedBlueprint.blueprint_id,
      })
    })
  }, [connectedAreas, inspectionArea, pointCloud2ds, pointSize, selectedBlueprint, loadPCD, setPointCloud2ds, dispatch])

  useEffect(() => {
    if (
      !selectedBlueprint?.blueprint_id ||
      selectedBlueprint.blueprint_id === initializedBlueprintId2DPcd.current ||
      !previousSelectedBlueprintId
    )
      return

    if (selectedBlueprint.blueprint_id !== previousSelectedBlueprintId) {
      setPointCloud2ds([])
      dispatch(setPcd2dBoundingBox(undefined))
      initializedBlueprintId2DPcd.current = null
    }
  }, [selectedBlueprint?.blueprint_id, previousSelectedBlueprintId, setPointCloud2ds, dispatch])

  useEffect(() => {
    if (!selectedBlueprint?.sort_key) return

    void load2DPointCloud()
  }, [selectedBlueprint?.sort_key, load2DPointCloud])

  /**
   * If the 2D PCD is still being processed, loop to check for progress.
   */
  useEffect(() => {
    const runningPcd = getRunningPcdStatus(selectedBlueprint)
    if (!project?.project_id || !inspectionArea?.inspection_area_id || runningPcd.length === 0) return

    // Reset existing ones first
    setPointCloud2ds([])
    dispatch(setPcd2dBoundingBox(undefined))
    initializedBlueprintId2DPcd.current = null

    const interval = 1000
    const intervalCap = 5000
    const timeout = 300000
    const startTime = Date.now()
    let retries = 0
    const loop = () =>
      withPromiseContained(async () => {
        // Timeout
        if (Date.now() - startTime > timeout) return

        const token = await getAccessToken()
        if (!token) return

        const result = await getBlueprints(token, project.project_id, inspectionArea.inspection_area_id)
        if (!result) return

        const blueprint = result.results.find((bp) => bp.blueprint_id === selectedBlueprint?.blueprint_id)
        if (!blueprint) return

        // If it is still running, loop again
        const hasRunningPcd = getRunningPcdStatus(blueprint).length > 0
        if (hasRunningPcd) {
          retries += 1
          setTimeout(loop, retries <= 3 ? interval : Math.min(interval * retries ** 2, intervalCap))
          return
        }

        dispatch(updateBlueprints([blueprint]))
      }).catch(() => {
        showErrorModal('ファイル変換に失敗しました。新しい設計図面を作成するか、サポートに連絡してください。')
      })

    void loop()
  }, [
    selectedBlueprint,
    project?.project_id,
    inspectionArea?.inspection_area_id,
    getAccessToken,
    showErrorModal,
    setPointCloud2ds,
    dispatch,
  ])

  /**
   * Load point cloud data on page load.
   */
  useEffect(() => {
    if (!inspectionArea?.inspection_area_id || pointClouds.length) return

    const loadStartTime = Date.now()
    void withPromiseContained(async () => {
      const pcds = (await Promise.all(loadAllPCDs(connectedAreas, null, loadPCD, () => null))).filter(
        (p) => p?.pcd,
      ) as {
        areaId: string
        pcd: Points | PointCloudOctree
      }[]

      if (!pcds.length) return

      setPointClouds(
        pcds
          .filter((p): p is { areaId: string; pcd: PointCloudOctree } => p.pcd instanceof PointCloudOctree)
          .map((p) => p.pcd),
      )

      // get bounding box center and radius
      const { bbox, radius: rds } = getCenterAndRadius(pcds.map((p) => p.pcd))
      dispatch(setPcdBoundingBox([bbox.min.toArray(), bbox.max.toArray()]))
      dispatch(setPcdBoundingSphereRadius(rds))

      if (pcds[0].pcd.material instanceof PointsMaterial) {
        dispatch(setPointSize(pcds[0].pcd.material.size))
        dispatch(setDefaultPointSize(pcds[0].pcd.material.size))
      }
    }).finally(() => {
      // Track PCD File Load Performance for 3D
      const loadDuration = Date.now() - loadStartTime
      mixpanel.track('PCD File Load Performance', {
        Type: '3D',
        'Duration (ms)': loadDuration,
        'Duration (s)': Math.round(loadDuration / 1000),
      })
    })
  }, [inspectionArea?.inspection_area_id, pointClouds, connectedAreas, setPointClouds, dispatch, loadPCD])

  /**
   * Auto-calculate default print scale when:
   * 1. A blueprint is selected that doesn't have a valid print_scale (undefined, null, or 0)
   * 2. The appropriate PCD bounding box is available (2D for 2D mode, fallback to 3D)
   */
  useEffect(() => {
    // Determine which bounding box to use based on mode and availability
    const boundingBoxToUse = mode === Mode.Editor2D && pcd2dBoundingBox ? pcd2dBoundingBox : pcdBoundingBox

    if (
      !selectedBlueprint?.sort_key ||
      !boundingBoxToUse ||
      !project?.project_id ||
      !inspectionArea?.inspection_area_id ||
      (selectedBlueprint.print_scale !== undefined &&
        selectedBlueprint.print_scale !== null &&
        selectedBlueprint.print_scale > 0) // Skip if already has valid print_scale
    ) {
      return
    }

    const calculateAndSetDefaultScale = async () => {
      try {
        const token = await getAccessToken()
        if (!token) return

        // Calculate default scale for A4 size using the appropriate bounding box
        const defaultScale = autoCalculatePrintScale(boundingBoxToUse, 'A4')

        // Update the blueprint with the calculated default scale
        const updatedBlueprint = {
          ...selectedBlueprint,
          print_scale: defaultScale,
        }

        // Update local store state
        dispatch(setSelectedBlueprint(updatedBlueprint))

        await dispatch(
          updateBlueprint({
            accessToken: token,
            projectId: project.project_id,
            areaId: inspectionArea.inspection_area_id,
            blueprint: updatedBlueprint,
          }),
        ).unwrap()
      } catch (error) {
        Sentry.captureException(error)
      }
    }

    void calculateAndSetDefaultScale()
  }, [
    selectedBlueprint,
    pcdBoundingBox,
    pcd2dBoundingBox,
    mode,
    project?.project_id,
    inspectionArea?.inspection_area_id,
    getAccessToken,
    dispatch,
  ])

  /**
   * Apply point size to the PCD material.
   *
   * @param pcd The point cloud object (Points or PointCloudOctree)
   * @param originalMaterial The original PointsMaterial to update (null for PointCloudOctree)
   * @param size The desired point size
   */
  const applyPointSize = useCallback(
    (pcd: Points | PointCloudOctree, originalMaterial: PointsMaterial | null, size: number) => {
      if (originalMaterial && pcd instanceof Points) {
        const mat = pcd.material as PointsMaterial
        if (mat?.size !== size) {
          const newSize = clamp(size || 1, 1, 10) * EDITOR_POINT_SIZE_INTERVAL
          mat.polygonOffset = true
          mat.polygonOffsetFactor = 1

          // Update both the saved and current material
          mat.size = newSize
          originalMaterial.size = newSize
        }
      } else if (pcd instanceof PointCloudOctree) {
        pcd.material.size = size || EDITOR_POINT_SIZE_INTERVAL
      }
    },
    [],
  )

  /**
   * Update point size in the material when pointSize changes.
   */
  useEffect(() => {
    pointClouds.forEach((pcd) => applyPointSize(pcd, null, pointSize))
    pointCloud2ds.forEach((pcd) => applyPointSize(pcd, null, pointSize))
  }, [pointClouds, pointCloud2ds, pointSize, applyPointSize])

  /**
   * If initCompleted is reset, reset the canvas.
   */
  useEffect(() => {
    if (initCompleted) return

    setPointClouds([])
    setPointCloud2ds([])
    arcballControls.current?.dispose()
    arcballControls.current = null
  }, [initCompleted, arcballControls, setPointClouds, setPointCloud2ds, dispatch])

  const applyShaders = useCallback(
    (pcd: PointCloudOctree | Points, pcdMode: Mode = Mode.Editor3D) => {
      const oldMaterial = pcd.material
      if (!oldMaterial) {
        return
      }

      const maskRegionUniforms: {
        modelMatrix: Matrix4
        min: Vector3
        max: Vector3
        opacity: number
      }[] = []

      // If there are no mask, we need to reset the PCD material.
      // On fresh load this would not be a problem, but if the last
      // region was deleted, the previous mask region will still be
      // applied so we need to reset it.
      if (pcdMode === Mode.Editor3D && selectedBlueprint?.masking_region) {
        maskRegionUniforms.push(generateCuboidUniforms(selectedBlueprint.masking_region))
      }

      // Add the cuboid we are creating/editing to the list of mask regions
      else if (workingMask) {
        maskRegionUniforms.push(generateCuboidUniforms(workingMask))
      }

      const showAsDim =
        selectedTool === BlueprintEditorTools.MaskPCD ||
        (BlueprintEditorTools.Focus === selectedTool && prevSelectedTool === BlueprintEditorTools.MaskPCD)

      if (pcd instanceof PointCloudOctree) {
        if (maskRegionUniforms.length) {
          pcd.material.maskRegionLength = maskRegionUniforms.length
          pcd.material.opacityOutOfMasks = showAsDim ? 0.1 : 0
          pcd.material.maskRegions = maskRegionUniforms
        } else {
          pcd.material.maskRegionLength = 0
          pcd.material.maskRegions = []
        }

        if (pcdTransparency !== undefined) {
          pcd.material.opacity = pcdTransparency
        }

        // enable transparency if we're editing, but use a different blending mode
        if ((showAsDim && maskRegionUniforms.length) || (pcdTransparency !== undefined && pcdTransparency < 1)) {
          pcd.material.enableTransparency()
          pcd.material.blending = NormalBlending
        } else {
          pcd.material.disableTransparency()
        }

        pcd.material.needsUpdate = true
        pcd.material.updateShaders()
        return
      }

      // Can't deal with  array Materials.
      if (!(oldMaterial instanceof PointsMaterial)) return

      // Always start with material that has not have its shaders already modified.
      const newMaterial = oldMaterial.clone()

      if (maskRegionUniforms.length) {
        newMaterial.onBeforeCompile = (shader) => {
          // Pass the model matrix to the shader as uniforms
          const uniforms = {
            maskRegion: { value: maskRegionUniforms },
            showAsDim: { type: 'b', value: showAsDim },
          }

          shader.uniforms = { ...(shader.uniforms || {}), ...uniforms }

          // Get the position of the fragment in world space by appending it to the beginning of the shader.
          // We still need the original shader for other parts of the rendering.
          shader.vertexShader = shader.vertexShader.replace(
            `void main() {`,
            `
              varying vec4 fragPosition;
  
              void main() {
                  fragPosition = modelMatrix * vec4(position, 1.0);
              `,
          )

          // Add a check if the fragment is outside the masks and if so, set a lower opacity
          // or discard it altogether.
          // Replace the original diffuseColor to use the new opacity.
          // The original shader is retained as we still need it to render it as-is,
          // just with lower opacity or none as necessary.
          shader.fragmentShader = shader.fragmentShader
            .replace(
              `void main() {`,
              `
                  #define maskRegionLength ${maskRegionUniforms.length}
                  #define override_opacity true
                
                  struct Region {
                    mat4 modelMatrix;
                    vec3 min;
                    vec3 max;
                  };
                  uniform bool showAsDim;
                  uniform Region maskRegion[maskRegionLength];
                  varying vec4 fragPosition;
  
                  bool checkWithin(Region region)
                  {
                    // we need fragment position localized to the mask region
                    vec4 localPos = region.modelMatrix * fragPosition;
  
                    // Check if the fragment is inside the cube in its local space
                    return all(greaterThanEqual(localPos.xyz, region.min)) && all(lessThanEqual(localPos.xyz, region.max));
                  }
  
                  void main() {
                    bool toDiscard = !showAsDim;
                    float defaultOpacity = opacity;
                    float updatedOpacity = showAsDim ? 0.1 : 0.0;
  
                    for(int i=0; i<maskRegionLength; i++)
                    {
                      if (checkWithin(maskRegion[i]))
                      {
                        updatedOpacity = defaultOpacity;
                        toDiscard = false;
                      }
                    }
  
                    if (toDiscard) {
                      discard;
                      return;
                    }
                `,
            )
            .replace(
              'vec4 diffuseColor = vec4( diffuse, opacity );',
              'vec4 diffuseColor = vec4( diffuse, updatedOpacity );',
            )
        }
      }

      if (pcdTransparency !== undefined) newMaterial.opacity = pcdTransparency

      // Shaders are compiled but when mask regions change, we need to recompile them.
      // Use IDs of the mask regions as a cache key.
      // If we are editing a cuboid, add that as well. Only need to do it once. If the cuboid changes,
      // uniforms will continue to be updated without needing to recompile the shader.
      newMaterial.needsUpdate = true
      newMaterial.customProgramCacheKey = () =>
        // prettier-ignore
        [
        `${maskRegionUniforms.length}`, 
        workingMask ? 'editing-cuboid' : '', 
        showAsDim ? 'pcd-trim' : ''
      ]
    .join(',')

      pcd.material = newMaterial
    },
    [selectedTool, prevSelectedTool, workingMask, pcdTransparency, selectedBlueprint?.masking_region],
  )

  useEffect(() => {
    pointClouds.forEach(applyShaders)
    pointCloud2ds.forEach((pcd) => applyShaders(pcd, Mode.Editor2D))
  }, [pointClouds, pointCloud2ds, applyShaders])

  /**
   * 3D PCDs are shown on following situations:
   * 1. In 3D mode
   * 2. During blueprint creation's FOCUS stage.
   */
  const is3DPCDVisible = useMemo(
    () =>
      !isPointCloudInvisible &&
      (mode === Mode.Editor3D ||
        (isCreatingBlueprint && [SelectionStage.FOCUS, SelectionStage.SAVING].includes(selectionStage))),
    [isPointCloudInvisible, mode, isCreatingBlueprint, selectionStage],
  )

  return (
    <group>
      <RenderLoop
        scene={sceneRef}
        potree={potree}
        pointClouds={mode === Mode.Editor3D || isCreatingBlueprint ? pointClouds : pointCloud2ds}
      />

      {pointClouds.length &&
        pointClouds.map((pcd) => <primitive object={pcd} visible={is3DPCDVisible} key={pcd.uuid} />)}
      {pointCloud2ds.length &&
        pointCloud2ds.map((pcd) => (
          <primitive object={pcd} visible={!isPointCloudInvisible && mode === Mode.Editor2D} key={pcd.uuid} />
        ))}

      <CanvasGrid size={pcdBoundingSphereRadius * 4} />
    </group>
  )
}

export default PCDManager
